import { IconButton, styled, Box, Tooltip } from "@mui/material";
import ConfirmationModal from "../ConfirmationModal";
import { MoreVert, Replay, CheckCircle, ClearOutlined, Link as LinkIcon, Assignment as AssignmentIcon, ShoppingCart as ShoppingCartIcon, LocalPhone as LocalPhoneIcon, WhatsApp as WhatsAppIcon } from "@mui/icons-material";
import React, { useContext, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/Auth/AuthContext";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import api from "../../services/api";
import { isFeatureOn, WABA_FLAGS } from "../../helpers/featureFlags";
import { gruposCompartilhados } from "../../helpers/groupsVisibility";
import useSettings from "../../hooks/useSettings";
import { Can } from "../Can";
import TicketOptionsMenu from "../TicketOptionsMenu";
import AcceptTicketWithouSelectQueue from "../AcceptTicketWithoutQueueModal";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import ForwardTicketToChatModal from "../ForwardTicketToChatModal";
import WabaCtaModal from "../WabaCtaModal";
import WabaFlowModal from "../WabaFlowModal";
import WabaProductModal from "../WabaProductModal";

const ActionButtonsContainer = styled(Box)(({ theme }) => ({
	marginRight: theme.spacing(1),
	flex: "none",
	alignSelf: "center",
	marginLeft: "auto",
	display: "flex",
	alignItems: "center",
	flexWrap: "nowrap",
	"& > *": {
		margin: theme.spacing(0.25),
	},
	[theme.breakpoints.down("sm")]: {
		marginRight: theme.spacing(0.5),
		marginLeft: 0,
		justifyContent: "flex-end",
		width: "auto",
		"& > *": {
			margin: theme.spacing(0.3),
		},
	},
}));

const ActionIconButton = styled(IconButton)(({ theme, color }) => ({
	borderRadius: "50%",
	padding: theme.spacing(0.75),
	"& .MuiSvgIcon-root": { fontSize: "1.25rem" },
	backgroundColor: color === "primary" ? theme.palette.primary.main : "transparent",
	color: color === "primary" ? theme.palette.primary.contrastText : theme.palette.primary.main,
	boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
	transition: "all 0.2s ease",
	"&:hover": {
		backgroundColor: color === "primary" 
			? theme.palette.primary.dark 
			: theme.palette.mode === "dark" ? theme.palette.action.hover : theme.palette.grey[200],
		boxShadow: "0 3px 6px rgba(0,0,0,0.15)",
	},
	[theme.breakpoints.down("sm")]: {
		padding: theme.spacing(0.7),
	},
}));

const LoadingIconButton = ({ loading, color, onClick, children, title }) => {
	const [isLoading, setIsLoading] = useState(loading);
	
	const handleClick = async (e) => {
		if (loading) return;
		
		setIsLoading(true);
		try {
			await onClick(e);
		} finally {
			setIsLoading(false);
		}
	};
	
	return (
		<Tooltip title={title} arrow placement="top">
			<span>
				<ActionIconButton
					color={color}
					disabled={isLoading}
					onClick={handleClick}
				>
					{children}
				</ActionIconButton>
			</span>
		</Tooltip>
	);
};

const TicketActionButtons = ({ ticket, onTicketAccepted }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [anchorEl, setAnchorEl] = useState(null);
	const [loading, setLoading] = useState(false);
const [acceptTicketWithouSelectQueueOpen, setAcceptTicketWithouSelectQueueOpen] = useState(false);
	const ticketOptionsMenuOpen = Boolean(anchorEl);
	const { user } = useContext(AuthContext);
	// Grupo compartilhado (groupsVisibility = all/queue) é de todos: atendente
	// não devolve nem finaliza — só admin (p/ limpar grupo morto).
	const { settings: allSettings } = useSettings();
	const grupoDeTodos =
		!!(ticket.contact?.isGroup || ticket.isGroup) &&
		gruposCompartilhados(allSettings) &&
		user?.profile !== "admin" &&
		user?.profile !== "masteradmin";
	const { whatsApps } = useContext(WhatsAppsContext);
	const [ctaModalOpen, setCtaModalOpen] = useState(false);
	const [flowModalOpen, setFlowModalOpen] = useState(false);
	const [productModalOpen, setProductModalOpen] = useState(false);
	const [forwardChatModalOpen, setForwardChatModalOpen] = useState(false);

	// Ações WABA OFICIAL: só em conexão oficial + com a flag ligada.
	const connection = (whatsApps || []).find(w => w.id === ticket.whatsappId);
	const isOfficial = ticket.status === "open" && !!connection?.isOfficialWaba;
	const showCta = isOfficial && isFeatureOn(WABA_FLAGS.CTA_URL);
	const showFlow = isOfficial && isFeatureOn(WABA_FLAGS.FLOWS);
	const showProduct = isOfficial && isFeatureOn(WABA_FLAGS.CATALOG);

	// Voz (WhatsApp Calling): botão "Ligar" só quando o softphone está online,
	// o ticket é individual e tem número. Escuta o status publicado pelo Softphone.
	const [voiceReady, setVoiceReady] = useState(!!window.__softphoneReady);
	useEffect(() => {
		const onStatus = e => setVoiceReady(!!e?.detail?.ready);
		window.addEventListener("softphone:status", onStatus);
		return () => window.removeEventListener("softphone:status", onStatus);
	}, []);
	// Ligar pelo WhatsApp só existe na conexão oficial (WABA): a Meta faz a chamada.
	const showCall =
		voiceReady &&
		isOfficial &&
		isFeatureOn(WABA_FLAGS.CALLING) &&
		ticket.status === "open" &&
		!ticket.isGroup &&
		!!ticket.contact?.number;

	// Telefone (tronco da operadora): mesmo softphone, sem depender da Meta.
	const showPhoneCall =
		voiceReady &&
		isFeatureOn(WABA_FLAGS.VOIP_PSTN) &&
		ticket.status === "open" &&
		!ticket.isGroup &&
		!!ticket.contact?.number;

	const handleCall = via => {
		// Ligação pelo WhatsApp só existe na API oficial (Calling API da Meta). Conversa de
		// conexão não oficial: avisa em vez de tentar (antes a Meta recusava e a tela dizia
		// que o cliente tinha recusado). Sapem 31/10/2026.
		if (via === "whatsapp" && !connection?.isOfficialWaba) {
			toast.info(
				`A ligação pelo WhatsApp só funciona em conversas de números na API oficial. Esta conversa é da conexão ${connection?.name || "atual"}, que não é oficial.`,
				{ autoClose: 9000 }
			);
			return;
		}
		window.dispatchEvent(
			new CustomEvent("softphone:call", {
				detail: {
					via,
					number: ticket.contact?.number,
					name: ticket.contact?.name,
					whatsappId: ticket.whatsappId,
					ticketId: ticket.id
				}
			})
		);
	};

	const handleOpenTicketOptionsMenu = e => {
		setAnchorEl(e.currentTarget);
	};

	const handleCloseTicketOptionsMenu = e => {
		setAnchorEl(null);
	};

	const handleUpdateTicketStatus = async (e, status, userId) => {
		setLoading(true);
		try {
			await api.put(`/tickets/${ticket.id}`, {
				status: status,
				userId: userId || null
			});
			
		
			
			if (status === "open") {
				const isGroup = ticket?.contact?.isGroup;
				const targetTab = isGroup ? "groups" : "open";
				localStorage.setItem("pressticket:changeTab", targetTab);
				navigate(`/tickets/${ticket.id}`);
			} else if (status === "pending") {
				navigate("/tickets");
			} else if (status === "closed") {
				navigate("/tickets");
			}
			
			setLoading(false);
		} catch (err) {
			setLoading(false);
			toastError(err);
		}
	};

	// Configuração "Pedir confirmação ao finalizar" (padrão desligado): evita
	// finalizar sem querer — o cliente some do Atendendo (Insetisan, set/2025).
	const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
	const pedirConfirmacao = (allSettings || []).some(
		s => s.key === "confirmCloseTicket" && s.value === "enabled"
	);
	const handleConfirmClose = () => {
		if (pedirConfirmacao) {
			setConfirmCloseOpen(true);
			return;
		}
		handleUpdateTicketStatus(null, "closed", user?.id);
	};

	const handleOpenAcceptTicketWithouSelectQueue = () => {
		setAcceptTicketWithouSelectQueueOpen(true);
	};

	const handleAcceptTicket = async (e) => {
		if (ticket.queue === null || ticket.queue === undefined) {
			handleOpenAcceptTicketWithouSelectQueue();
		} else {
			await handleUpdateTicketStatus(e, "open", user?.id);
			if (onTicketAccepted) {
				await onTicketAccepted();
			}
		}
	};

	return (
		<ActionButtonsContainer>
			<ConfirmationModal
				title="Finalizar este atendimento?"
				open={confirmCloseOpen}
				onClose={() => setConfirmCloseOpen(false)}
				onConfirm={() => handleUpdateTicketStatus(null, "closed", user?.id)}
			>
				O ticket sai de Atendendo e vai para Resolvidos.
			</ConfirmationModal>
			{ticket.status === "closed" && (
				<LoadingIconButton
					title={t("messagesList.header.buttons.reopen")}
					loading={loading}
					onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
				>
					<Replay />
				</LoadingIconButton>
			)}
			{ticket.status === "open" && (
				<>
					{!grupoDeTodos && (
					<LoadingIconButton
						title={t("messagesList.header.buttons.return")}
						loading={loading}
						onClick={e => handleUpdateTicketStatus(e, "pending", null)}
					>
						<Replay />
					</LoadingIconButton>
					)}
					{!grupoDeTodos && (
					<LoadingIconButton
						title={t("messagesList.header.buttons.resolve")}
						loading={loading}
						color="primary"
						onClick={handleConfirmClose}
					>
						<ClearOutlined />
					</LoadingIconButton>
					)}
					{showCta && (
						<Tooltip title="Enviar botão de link (WhatsApp oficial)" arrow placement="top">
							<ActionIconButton color="primary" onClick={() => setCtaModalOpen(true)}>
								<LinkIcon />
							</ActionIconButton>
						</Tooltip>
					)}
					{showFlow && (
						<Tooltip title="Enviar formulário (WhatsApp Flow)" arrow placement="top">
							<ActionIconButton color="primary" onClick={() => setFlowModalOpen(true)}>
								<AssignmentIcon />
							</ActionIconButton>
						</Tooltip>
					)}
					{showProduct && (
						<Tooltip title="Enviar produto do catálogo" arrow placement="top">
							<ActionIconButton color="primary" onClick={() => setProductModalOpen(true)}>
								<ShoppingCartIcon />
							</ActionIconButton>
						</Tooltip>
					)}
					{showCall && (
						<Tooltip title="Ligar pelo WhatsApp" arrow placement="top">
							<ActionIconButton onClick={() => handleCall("whatsapp")} style={{ color: "#25D366" }}>
								<WhatsAppIcon />
							</ActionIconButton>
						</Tooltip>
					)}
					{showPhoneCall && (
						<Tooltip title="Ligar por telefone (fixo/celular)" arrow placement="top">
							<ActionIconButton color="primary" onClick={() => handleCall("telefone")}>
								<LocalPhoneIcon />
							</ActionIconButton>
						</Tooltip>
					)}
					<Tooltip title="Pedir ajuda da equipe no chat interno" arrow placement="top">
						<ActionIconButton onClick={() => setForwardChatModalOpen(true)}>
							<ForumOutlinedIcon />
						</ActionIconButton>
					</Tooltip>
					<Tooltip title={t("messagesList.header.buttons.options")} arrow placement="top">
						<ActionIconButton
							onClick={handleOpenTicketOptionsMenu}>
							<MoreVert />
						</ActionIconButton>
					</Tooltip>
					<TicketOptionsMenu
						ticket={ticket}
						anchorEl={anchorEl}
						menuOpen={ticketOptionsMenuOpen}
						handleClose={handleCloseTicketOptionsMenu}
					/>
				</>
			)}
			<Can
				role={user.profile}
				perform="drawer-admin-items:view"
				yes={() => (
					<>
						{ticket.status === "pending" && (
							<LoadingIconButton
								title={t("messagesList.header.buttons.accept")}
								loading={loading}
								color="primary"
								onClick={handleAcceptTicket}
							>
								<CheckCircle />
							</LoadingIconButton>
						)}
					</>
				)}
			/>
			<ForwardTicketToChatModal
				open={forwardChatModalOpen}
				onClose={() => setForwardChatModalOpen(false)}
				ticketId={ticket.id}
			/>
			<AcceptTicketWithouSelectQueue
				modalOpen={acceptTicketWithouSelectQueueOpen}
				onClose={(e) => setAcceptTicketWithouSelectQueueOpen(false)}
				ticketId={ticket.id}
				onSuccess={onTicketAccepted}
			/>
			<WabaCtaModal
				open={ctaModalOpen}
				onClose={() => setCtaModalOpen(false)}
				whatsappId={ticket.whatsappId}
				to={ticket.contact?.number}
				ticketId={ticket.id}
			/>
			<WabaFlowModal
				open={flowModalOpen}
				onClose={() => setFlowModalOpen(false)}
				whatsappId={ticket.whatsappId}
				to={ticket.contact?.number}
				ticketId={ticket.id}
			/>
			<WabaProductModal
				open={productModalOpen}
				onClose={() => setProductModalOpen(false)}
				whatsappId={ticket.whatsappId}
				to={ticket.contact?.number}
				ticketId={ticket.id}
			/>
		</ActionButtonsContainer>
	);
};

export default TicketActionButtons;
